import Box from '@mui/material/Box'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import Collapse from '@mui/material/Collapse'
import { NavLink, useLocation } from 'react-router-dom'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { useState, useEffect } from 'react'
import { useSidebar } from '../../context/SidebarContext'
import { useLocale } from '../../i18n/useLocale'
import { LocaleCode } from '../../i18n'

interface NavItem {
  label: string
  path: string
  locales?: LocaleCode[]
  children?: NavItem[]
}

interface NavSection {
  title: string
  basePath: string
  items: NavItem[]
}

const navigation: NavSection[] = [
  {
    title: 'Getting Started',
    basePath: '/getting-started',
    items: [
      { label: 'Overview', path: '/getting-started/overview' },
      { label: 'Authentication', path: '/getting-started/authentication' },
      { label: 'Quick Start', path: '/getting-started/quick-start' },
    ],
  },
  {
    title: 'Integration Guides',
    basePath: '/guides',
    items: [
      { label: 'Parcel Creation', path: '/guides/parcel-creation' },
      { label: 'Label Generation', path: '/guides/label-generation' },
      { label: 'Parcel Tracking', path: '/guides/parcel-tracking' },
      { label: 'Billing & Payments', path: '/guides/payments' },
    ],
  },
  {
    title: 'Widgets',
    basePath: '/widgets',
    items: [
      { label: 'APM Finder Widget', path: '/widgets/apm-finder' },
    ],
  },
  {
    title: 'API Reference',
    basePath: '/api-reference',
    items: [
      { label: 'Parcels', path: '/api-reference/parcel-api' },
      { label: 'Labels', path: '/api-reference/label-api' },
      { label: 'Tracking', path: '/api-reference/status-api' },
    ],
  },
  {
    title: 'Testing',
    basePath: '/testing',
    items: [
      { label: 'Sandbox Environment', path: '/testing/sandbox' },
    ],
  },
  {
    title: 'Support',
    basePath: '/support',
    items: [
      { label: 'Help & FAQ', path: '/support' },
    ],
  },
]

export default function Sidebar() {
  const location = useLocation()
  const { isMobile, close } = useSidebar()
  const { currentLocale } = useLocale()
  const [openSections, setOpenSections] = useState<string[]>([])

  useEffect(() => {
    // Auto-expand the section that contains the current path
    const currentSection = navigation.find((section) =>
      location.pathname.startsWith(section.basePath)
    )
    if (currentSection && !openSections.includes(currentSection.basePath)) {
      setOpenSections((prev) => [...prev, currentSection.basePath])
    }
  }, [location.pathname])

  const toggleSection = (basePath: string) => {
    setOpenSections((prev) =>
      prev.includes(basePath)
        ? prev.filter((p) => p !== basePath)
        : [...prev, basePath]
    )
  }

  const handleNavClick = () => {
    if (isMobile) {
      close()
    }
  }

  return (
    <Box
      component="nav"
      sx={{
        width: 280,
        flexShrink: 0,
        bgcolor: 'grey.50',
        borderRight: '1px solid',
        borderColor: 'grey.200',
        overflowY: 'auto',
        height: 'calc(100vh - 64px)',
      }}
    >
      <List sx={{ py: 2 }}>
        {navigation.map((section) => {
          const isOpen = openSections.includes(section.basePath)
          const isActiveSection = location.pathname.startsWith(section.basePath)

          return (
            <Box key={section.basePath} sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => toggleSection(section.basePath)}
                sx={{
                  py: 1,
                  px: 2,
                  mx: 1,
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'grey.100' },
                }}
              >
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: isActiveSection ? 'primary.main' : 'text.primary',
                        textTransform: 'uppercase',
                        fontSize: '0.7rem',
                        letterSpacing: '0.05em',
                      }}
                    >
                      {section.title}
                    </Typography>
                  }
                />
                {isOpen ? (
                  <ExpandLess sx={{ fontSize: 18, color: 'text.secondary' }} />
                ) : (
                  <ExpandMore sx={{ fontSize: 18, color: 'text.secondary' }} />
                )}
              </ListItemButton>

              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {section.items
                    .filter(item => !item.locales || item.locales.includes(currentLocale))
                    .map((item) => (
                    <ListItemButton
                      key={item.path}
                      component={NavLink}
                      to={item.path}
                      onClick={handleNavClick}
                      className={location.pathname === item.path ? 'active' : ''}
                      sx={{ pl: 4, py: 0.75 }}
                    >
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          variant: 'body2',
                          sx: { fontSize: '0.875rem' },
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </Box>
          )
        })}
      </List>
    </Box>
  )
}
